import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import * as d3 from 'd3'
import { useGetSize } from 'Hooks/useGetSize'
import { AnimatedNote } from '../ExampleComponent/AnimatedNote'
import { GridLine } from 'theme/styles'

const RetiredPctChart = ({ year }: { year: number }) => {
  const [data, setData] = useState<RetiredPctData[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const isMobile = window.innerWidth < 768
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width : width * 0.75

  const minX = d3.min(data, (d) => d.year) ?? 0
  const maxX = d3.max(data, (d) => d.year) ?? 0
  const maxY = d3.max(data, (d) => d.pct) ?? 0

  const xDomain = [minX, maxX]
  const yDomain = [0, maxY + maxY * 0.1]
  const margin = {
    top: 30,
    right: 0,
    bottom: 30,
    left: 50,
  }
  const tickYLength = 18
  const tickXLength = 12

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const lineGenerator = d3.line() //.curve(d3.curveCatmullRom.alpha(0.5))

  const ageDataLine: [number, number][] = data.map((d) => {
    return [xScale(d.year), yScale(d.pct)]
  })

  const ageDataArea = [...ageDataLine]

  ageDataArea.unshift([xScale(minX), height - margin.bottom])
  ageDataArea.push([width - margin.right, height - margin.bottom])

  const ageLinePath = lineGenerator(ageDataLine) || undefined
  const ageAreaPath = lineGenerator(ageDataArea) || undefined

  const xTicks = data
    .filter((d) => d.year % 30 === 0 || d.year === minX)
    .map((d) => d.year)
  const yTicks = [0, 10, 20, 30, 40]

  console.log(xTicks)

  useEffect(() => {
    // Retired PCT data
    d3.csv<RetiredPctData>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2024/votations-federales-avs'
          : ''
      }/retired_pct_data.csv`,
      (d) => {
        return {
          year: +d['year'],
          pct: +d['pct_retired'],
        }
      }
    )
      .then((data) => {
        setData(data)
      })
      .catch((error) => {
        console.error('Error fetching the CSV data:', error)
      })
  }, [])

  return (
    <Container ref={containerRef}>
      <Svg ref={svgRef} width={width} height={height}>
        {/* Y grid */}
        {/* {xTicks.map((value) => (
            <GridLine
              x1={xScale(value)}
              x2={xScale(value)}
              y1={margin.top}
              y2={height - margin.bottom}
            />
          ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        {/* Y ticks */}
        {/* {yTicks.map((value) => {
            return (
              <Tick
                x1={margin.left - tickYLength - 20}
                x2={margin.left - 20}
                y1={yScale(value)}
                y2={yScale(value)}
              />
            )
          })} */}

        <Area d={ageAreaPath} />

        {/* <Line x1={xScale(1991)} y1={0 + margin.top} x2={xScale(1991)} y2={height - margin.bottom} />
        <Line x1={xScale(2022)} y1={0 + margin.top} x2={xScale(2022)} y2={height - margin.bottom} /> */}

        <MarkerArea
          x={xScale(2020)}
          y={margin.top}
          width={width - margin.right - xScale(2040)}
          height={height - margin.bottom - margin.top}
        />

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value) => (
        <Label
          style={{
            top: yScale(value) - 28,
            left: -30,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 50,
          }}
        >
          {value.toLocaleString('fr-CH')}
        </Label>
      ))}

      <Label
        style={{
          top: 0,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 200,
        }}
      >
        <strong>
          Nombre de personnes de 65+ pour 100 personnes de 20 à 64 ans
        </strong>
      </Label>

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height }}>{value}</Label>
      ))}

      <AnimatedNote show={true} position={[yScale(35), xScale(2025)]}>
        Accélération rapide du rapport de dépendance entre{' '}
        <strong>2020 et 2030</strong> avec l'arrivée des baby-boomers à la
        retraite
      </AnimatedNote>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const Line = styled.line`
  stroke: var(--text-color);
  stroke-width: 2px;
  stroke-dasharray: 2px 5px;
  stroke-linecap: round;
  opacity: 0.25;
`

const Area = styled.path`
  stroke: none;
  fill: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const MarkerArea = styled.rect`
  fill: ${({ theme }) => `rgb(${theme.mainColor}, 0.2)`};
  stroke: none;
  mix-blend-mode: multiply;
`

const Tick = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const BottomLine = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Label = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  color: var(--text-color);
  transform: translate(-50%, -50%);
  font-size: 0.8em;
  line-height: 1.1;
`

const Svg = styled.svg``

export default RetiredPctChart
